import { useEffect, useState } from 'react';

/**
 * "A new version is available — reload."
 *
 * The app is a single page: the JavaScript loaded when the tab opened keeps
 * running until the tab reloads, however many deploys happen in between. A tab
 * left open through a deploy quietly runs yesterday's flow against today's API
 * — which is how a feature that had just shipped looked like it "wasn't
 * working". Vite names the main bundle by content hash, so comparing the hash
 * in the served index.html with the one this tab loaded says whether a newer
 * build exists.
 */

const CHECK_EVERY_MS = 5 * 60 * 1000;

function currentBundle(): string | null {
  const script = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]');
  return script?.getAttribute('src') ?? null;
}

async function servedBundle(): Promise<string | null> {
  try {
    const res = await fetch('/index.html?v=' + Date.now(), { cache: 'no-store' });
    if (!res.ok) return null;
    const html = await res.text();
    const m = /<script[^>]+type="module"[^>]+src="([^"]*\/assets\/[^"]+)"/.exec(html);
    return m?.[1] ?? null;
  } catch {
    return null;
  }
}

export function UpdateBanner() {
  const [stale, setStale] = useState(false);

  useEffect(() => {
    const loaded = currentBundle();
    if (!loaded) return;
    let cancelled = false;
    const check = async () => {
      const served = await servedBundle();
      if (!cancelled && served && served !== loaded) setStale(true);
    };
    const timer = setInterval(() => void check(), CHECK_EVERY_MS);
    // Coming back to a tab is when a deploy is most likely to have happened.
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  if (!stale) return null;
  return (
    <div
      role="status"
      style={{
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        justifyContent: 'center',
        padding: '8px 16px',
        background: 'var(--accent, #2E86AB)',
        color: '#fff',
        fontSize: 13,
      }}
    >
      <span>A new version of the app is available.</span>
      <button
        type="button"
        className="btn-secondary"
        style={{ padding: '4px 10px', fontSize: 12 }}
        onClick={() => window.location.reload()}
      >
        Reload now
      </button>
    </div>
  );
}
